import axios from 'axios';
import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import Footer from '../component/Footer';
import { CheckOutlined, CrownOutlined } from '@ant-design/icons';

export default function Pricing() {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cookies] = useCookies(['user']);
    const navigate = useNavigate();

    const fetchPackages = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/pricing/active`);
            setPackages(response?.data || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPackages();
    }, []);

    const formatPrice = (price) => {
        return new Intl.NumberFormat('vi-VN').format(price) + 'đ';
    };

    const handleSelect = (pkg) => {
        if (cookies?.user) {
            navigate(`/profile/${cookies.user.id}`);
        } else {
            navigate('/login');
        }
    };

    const colors = [
        { gradient: 'from-blue-500 to-blue-700', light: 'bg-blue-50', text: 'text-blue-600', btn: 'bg-blue-500 hover:bg-blue-600' },
        { gradient: 'from-green-500 to-emerald-700', light: 'bg-green-50', text: 'text-green-600', btn: 'bg-green-500 hover:bg-green-600' },
        { gradient: 'from-purple-500 to-purple-700', light: 'bg-purple-50', text: 'text-purple-600', btn: 'bg-purple-500 hover:bg-purple-600' },
        { gradient: 'from-orange-500 to-red-600', light: 'bg-orange-50', text: 'text-orange-600', btn: 'bg-orange-500 hover:bg-orange-600' },
    ];

    return (
        <>
            <div className="min-h-screen bg-[#0a1628]">
                {/* Hero */}
                <div className="bg-gradient-to-r from-[#0f1e35] to-[#1a2d4a] text-white py-20 border-b border-white/10">
                    <div className="max-w-screen-xl mx-auto px-6 text-center">
                        <h1 className="text-4xl md:text-5xl font-extrabold mb-4">Bảng Giá</h1>
                        <p className="text-lg text-blue-300 max-w-2xl mx-auto">Chọn gói phù hợp để trải nghiệm đầy đủ tính năng học tiếng Anh hiệu quả</p>
                    </div>
                </div>

                <div className="max-w-screen-xl mx-auto px-6 py-12">
                    {loading ? (
                        <div className="py-20"><div className="flex justify-center"><div className="animate-spin rounded-full border-4 border-t-transparent border-blue-500 h-10 w-10"></div></div></div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                            {packages.map((pkg, index) => {
                                const color = colors[index % colors.length];
                                const features = pkg.features ? pkg.features.split('\n').filter(f => f.trim()) : [];
                                return (
                                    <div key={pkg.id} className={`relative bg-[#0f1e35] border rounded-2xl hover:-translate-y-2 transition-all overflow-hidden ${pkg.is_popular ? 'border-blue-500' : 'border-white/10 hover:border-white/20'}`}>
                                        {pkg.is_popular ? (
                                            <div className="absolute top-0 right-0 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold px-4 py-1 rounded-bl-xl">
                                                <CrownOutlined /> PHỔ BIẾN
                                            </div>
                                        ) : null}
                                        <div className={`bg-gradient-to-br ${color.gradient} text-white p-6 text-center`}>
                                            <h3 className="text-xl font-bold mb-2">{pkg.name}</h3>
                                            <div className="text-4xl font-extrabold mb-1">{formatPrice(pkg.price)}</div>
                                            <p className="text-sm opacity-80">{pkg.duration_days} ngày</p>
                                        </div>
                                        <div className="p-6">
                                            <p className="text-gray-400 text-sm mb-4">{pkg.description}</p>
                                            <ul className="space-y-3 mb-6">
                                                {features.map((feature, i) => (
                                                    <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                                                        <CheckOutlined className="text-green-400 mt-0.5 flex-shrink-0" />
                                                        <span>{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                            <button
                                                onClick={() => handleSelect(pkg)}
                                                className={`w-full ${color.btn} text-white font-bold py-3 rounded-xl transition-colors shadow-md`}
                                            >
                                                Đăng ký ngay
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* FAQ */}
                    <div className="bg-[#0f1e35] border border-white/10 rounded-2xl p-8 md:p-12">
                        <h2 className="text-2xl font-bold text-white mb-6 text-center">Câu hỏi thường gặp</h2>
                        <div className="space-y-4 max-w-3xl mx-auto">
                            {[
                                { q: 'Tôi có thể thanh toán bằng cách nào?', a: 'Chúng tôi hỗ trợ chuyển khoản ngân hàng và ví điện tử (Momo, ZaloPay).' },
                                { q: 'Có được hoàn tiền không?', a: 'Bạn có thể yêu cầu hoàn tiền trong 7 ngày đầu nếu không hài lòng.' },
                                { q: 'Gói VIP có gì khác biệt?', a: 'Gói VIP cho phép truy cập tất cả bài học, tải offline, và nhận hỗ trợ ưu tiên từ giáo viên.' },
                                { q: 'Tôi có thể nâng cấp gói không?', a: 'Có, bạn có thể nâng cấp bất cứ lúc nào và chỉ trả phần chênh lệch.' },
                            ].map((faq, index) => (
                                <div key={index} className="border border-white/10 rounded-xl p-5 hover:bg-white/5 transition-colors">
                                    <h3 className="font-bold text-white mb-2">{faq.q}</h3>
                                    <p className="text-gray-400 text-sm">{faq.a}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
}
